import { memo, useCallback, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, Stack, Typography } from '@mui/material';

import { UserPreferenceName } from '@/modules/userPreferences';
import { useUpdateUserPreferences } from '@/modules/userPreferences/hooks';
import { useConfig } from '@/modules/common/hooks';
import { SelectInput } from '@/modules/common/components/inputs';
import { assertNever } from '@/helpers/utils';

/**
 * @description
 * This is something that needs a proper solution; 'next pi' ;)
 * Steps to add release note;
 * 1. create a new component for the release note content (e.g. Release3p14p0) and add the content there
 * 2. Add the component to the activeNotes constant and create a new entry in the RELEASE_TAGS array for it
 * 3. Update the date in the 'latest' tag and update the date in the DigitalTwin.tsx file in the 'isAfter' to a date ~3 weeks after the release.
 * 4. Update the date in this file, should look something like this:           {activeTag === latestTag ? `${activeTag} - latest (25/02/2026)` : activeTag}
 * 5. Verify it automatically opens by setting the REACT_APP_VERSION in the setting.js to something like 100.0.0.
 */

const RELEASE_TAGS = ['4.1.9', '4.0.0', '3.15.0', '3.14.0', '3.13.0', '3.12.0'] as const;
type Tag = (typeof RELEASE_TAGS)[number];
const latestTag = RELEASE_TAGS[0];

const releaseNoteTextSx = { fontSize: '14px', whiteSpace: 'pre-wrap' } as const;
const releaseNoteListTextSx = { fontSize: '14px' } as const;

const releaseNoteListStyle = {
  paddingLeft: '20px',
  margin: 0,
} satisfies CSSProperties;

const latestTagButtonStyle = {
  textDecoration: 'underline',
  cursor: 'pointer',
} satisfies CSSProperties;

const trafficRulesUrl =
  'https://thive.sharepoint.com/:v:/r/sites/Teehive/Gedeelde%20documenten/General/04.%20PI%20sessions/PI-2026-Q2%20Remote/ReportIn/Vulcan/traffic%20rules.mov?csf=1&web=1&e=dATsGc';

const sprinklersUrl =
  'https://thive.sharepoint.com/:v:/r/sites/Teehive/Gedeelde%20documenten/General/04.%20PI%20sessions/PI-2026-Q2%20Remote/ReportIn/Vulcan/SprintDemosWK07/02.%20lassie-sprinklers-demo.mp4?csf=1&web=1&e=XaqnvV';

const versionSelectOptions = RELEASE_TAGS.map((tag) => ({
  label: tag === latestTag ? `${tag} - latest` : tag,
  value: tag,
}));

type Props = {
  closeModal: () => void;
};

const ReleaseNotesModalComponent = ({ closeModal }: Props) => {
  const { t } = useTranslation(['interface', 'common']);
  const { updateUserPreference } = useUpdateUserPreferences();
  const { app } = useConfig();
  const ref = useRef<HTMLDivElement | null>(null);

  const [activeTag, setActiveTag] = useState<Tag>(latestTag);

  const activeNotes = useMemo(() => {
    switch (activeTag) {
      case '4.1.9':
        return <Release4p1p9 />;
      case '4.0.0':
        return <Release4p0p0 />;
      case '3.15.0':
        return <Release3p15p0 />;
      case '3.14.0':
        return <Release3p14p0 />;
      case '3.13.0':
        return <Release3p13p0 />;
      case '3.12.0':
        return <Release3p12p0 />;
      default:
        assertNever(activeTag);
    }
  }, [activeTag]);

  const handleDontShowAgain = useCallback(() => {
    updateUserPreference(UserPreferenceName.APP_STARTUP_MESSAGE_VERSION, app.version);
    closeModal();
  }, [app.version, closeModal, updateUserPreference]);

  const handleGoToLatestVersion = useCallback(() => {
    setActiveTag(latestTag);
  }, []);

  const goToLatestVersionBtn = useMemo(
    () => (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <span onClick={handleGoToLatestVersion}>
        (<span style={latestTagButtonStyle}>{latestTag}</span>)
      </span>
    ),
    [handleGoToLatestVersion],
  );

  return (
    <Stack direction='column' spacing={2} sx={{ width: '60vw', maxWidth: '800px' }}>
      <Stack
        direction='column'
        spacing={2}
        padding={1}
        maxHeight='500px'
        width='100%'
        overflow='auto'
      >
        <Stack direction='row' justifyContent='space-between' alignItems='center' ref={ref}>
          <Typography variant='h3'>Release Notes</Typography>
          <Box component='div'>
            <SelectInput
              name='release version'
              onChange={setActiveTag}
              value={activeTag}
              options={versionSelectOptions}
              disabled={false}
            />
          </Box>
        </Stack>
        <Typography sx={releaseNoteTextSx}>
          A new version of Digital Twin has been released!
          {activeTag !== latestTag && goToLatestVersionBtn}
        </Typography>
        <Typography variant='h3'>
          {activeTag === latestTag ? `${activeTag} - latest (07/10/2026)` : activeTag}
        </Typography>
        {activeNotes}
      </Stack>

      <Stack direction='row' justifyContent='space-between'>
        <Button onClick={handleDontShowAgain}>Don&apos;t show again</Button>
        <Button onClick={closeModal}>{t('common:close', 'Close')}</Button>
      </Stack>
    </Stack>
  );
};

const Release4p1p9 = () => (
  <Typography component='div' sx={releaseNoteListTextSx}>
    <p>Dear user,</p>

    <p>
      Welcome to version 4.1.9 of the Digital Twin. This release includes many new features and
      improvements.
    </p>

    <Typography
      component='p'
      sx={{
        bgcolor: 'action.selected',
        borderLeft: '4px solid',
        borderColor: 'info.main',
        borderRadius: 1,
        px: 2,
        py: 1.5,
        my: 2,
      }}
    >
      As with any release, we have done a decent &apos;quality assurance&apos;, but it is not
      possible for us to test every possible combination/situation. So, some initial issues will
      occur. Please contact support@t-hive.io or make a ticket on{' '}
      <Link
        href='https://t-hive.freshdesk.com/support/login'
        target='_blank'
        rel='noopener noreferrer'
        underline='always'
      >
        the portal
      </Link>
      if you experience any problems, and know that identifying and resolving these issues will be
      our highest priority. Please now that all your tickets are highly appreciated and acted on.
    </Typography>
    <p>Bug-fixes (12 & 13/10/2026):</p>
    <ul style={releaseNoteListStyle}>
      <li>Fixed renaming areas causing a validation error</li>
      <li>Fixed a issue where connections suddenly disappeared</li>
      <li>Added connection orbs to make possible connections easier to identify</li>
      <li>Limited automatic highway cutouts to angle differences greater than 30°</li>
      <li>Clarified the shuttle notification message</li>
      <li>Fix racks not always showing up</li>
      <li>Apply same opacity to reference image in visualization</li>
      <li>Fixed bugs with teleporting (manual intervention)</li>
      <li>Fixed bug in Viking library (rounding error)</li>
      <li>Routing in FMS based on time instead of distance</li>
    </ul>
    <p>Bug-fixes (09/10/2026):</p>
    <ul style={releaseNoteListStyle}>
      <li>Fix vehicle limits on highways not being applied</li>
      <li>Fix origin not being intractable</li>
      <li>Fix floorplan not becoming intractable when updating vehicles</li>
      <li>
        Disallow generating shuttle template with a different shuttle vehicle type and load type
      </li>
      <li>Allow parking limited to Singular Station Areas of type Intake</li>
      <li>Fix angled highways not generating Pois</li>
      <li>Fix dashboards for individual simulations not being provided</li>
      <li>Fix msal authentication issues</li>
    </ul>

    <p>Some of the new features and improvements include:</p>
    <ul style={releaseNoteListStyle}>
      <li>
        <strong>Pre-announce &amp; Dynamic parking</strong> - You can now enable dynamic parking for
        an intake area, allowing vehicles to wait there. With the new pre-announce input on a flow,
        transport flows can head to the intake proactively, improving simulation efficiency.
      </li>
      <li>
        <strong>Improved performance</strong> - This update brings significant improvements to UX
        performance. This will be a continuous improvement activity for us the coming months.
      </li>

      <li>
        <strong>Curve splitting</strong> - Allows a waiting vehicle to go once a crossing vehicle
        has passed, instead of waiting for the entire spline to be complete.
      </li>
      <li>
        <strong>Added search bar</strong> - To help you look for your areas and points of interest,
        we have added a search-bar to the layers-accordion. This should make navigation and finding
        specific elements much easier and more efficient.
      </li>
      <li>
        <strong>Obstacle avoidance</strong> - We&apos;ve added our first step in obstacle avoidance
        so your vehicles will drive around walls, obstacles and stations. This option will be
        disabled by default and can be enabled in the vehicle-config-popover. In addition to this we
        improved our spline-view in the export runtime stack zip.
      </li>
      <li>
        <strong>Obstacle detection & safety fields</strong> - Vehicles now respond to more of their
        surroundings:
        <ul>
          <li>
            Safety scanners detect obstacles as well as other vehicles, slowing down when needed.
            We&apos;ll continue refining this new behavior in upcoming releases.
          </li>
          <li>
            Vehicle types without safety fields in their UVT now receive a default safety field that
            follows the vehicle&apos;s shape.
          </li>
        </ul>
      </li>
      <li>
        <strong>Simulation FMS updates</strong>
      </li>
      <ul>
        <li>
          Improved vehicle obstruction and deadlock handling. Vehicles will wait on other vehicles
          that they are blocked by if these are still in a moving state instead of re-routing
          immediately.
        </li>
        <li>
          Improved &apos;hops to wait before station&apos; feature, where vehicles will wait if
          their approach is blocked by another vehicle. This reduces the number of deadlocks that
          happen in front of stations.
        </li>
        <li>
          Improved path planning to prevent &apos;fish behavior&apos;. Vehicles should keep their
          lanes better and only divert if they are actually blocked by a vehicle.
        </li>
      </ul>
      <li>
        <strong>Improved in/export</strong> - Before, order profiles, FMS and vehicle configs were
        lost on export. This is now included in the export-zip and imported.
      </li>
      <li>
        <strong>Hik storage</strong> - We&apos;ve added a new template for easily generating
        hik-storages and we have added the vehicles to the vehicle selection. This feature is in
        alpha-phase, so highly likely not perfect yet. If you encounter issues using this template,
        please reach out to us.
      </li>
      <li>
        <strong>Stricter shuttle selection</strong> - There were some issues when generating a
        shuttle rack. To address this, the selection process has been made stricter, reducing errors
        and improving overall reliability.
      </li>
      <li>
        <strong>Added 2 vehicles</strong> - Added vehicles for selection; &apos;2-ULD runner
        (LR02)&apos; & &apos;Oppent Ecart 7 (ECART7)&apos;
      </li>
    </ul>

    <p>
      Our current focus will remain on improving the overall user experience and addressing any
      issues that arise promptly. The connections are in our sights for the next release, as well as
      multi-editing areas, better error-handling and more performance improvements.
    </p>

    <p>Notable bug-fixes in this release:</p>
    <ul style={releaseNoteListStyle}>
      <li>Fix double requests</li>
      <li>Fix resetting of highways when multiple vehicles are selected</li>
      <li>Fix &apos;ghost&apos; connections</li>
      <li>Fix version bumping</li>
      <ul>
        <li>Stop version bumping when going to visualization</li>
        <li>Stop version bumping after validating</li>
      </ul>

      <li>Fix copy/paste issues</li>
      <ul>
        <li>place newly pasted area in front</li>
        <li>select newly pasted area</li>
      </ul>
      <li>Fix obstacles not invalidating floorplan</li>
      <li>Fix number-inputs over committing bogging down shuttle-regeneration</li>
      <li>Fix &apos;racing&apos; inputs, causing re-selection and updating wrong areas</li>
      <li>Add back POIs &amp; groups to layers</li>
      <li>Fix cutouts not ever being send</li>
    </ul>
  </Typography>
);
const Release4p0p0Component = () => (
  <Typography component='div' sx={releaseNoteListTextSx}>
    <p>Dear user,</p>

    <p>
      Welcome to the new major version of the Digital Twin. This is our largest release to date and
      includes a wide range of new features, upgrades, and performance improvements.
    </p>
    <p>
      As with any major release, some initial issues may occur. Please contact{' '}
      <b>support@t-hive.io</b> or make a ticket on{' '}
      <Link
        href='https://t-hive.freshdesk.com/support/login'
        target='_blank'
        rel='noopener noreferrer'
        underline='always'
      >
        the portal
      </Link>{' '}
      if you experience any problems, and know that identifying and resolving these issues will be
      our highest priority.
    </p>

    <p>Some of the new features include:</p>

    <ul style={releaseNoteListStyle}>
      <li>
        <strong>Greatly increased performance</strong> - Loading floorplans, browser performance,
        and generating splines are now significantly faster.
      </li>
      <li>
        <strong>Constellation controls</strong> - You now have much more fine-grained control over
        vehicle positioning, including support for chevron parking. If you can think it, you can
        draw it.
      </li>
      <li>
        <strong>Preferences per floorplan</strong> - Every floorplan is different. Settings now
        persist per floorplan instead of being applied only at user level.
      </li>
      <li>
        <strong>Level of detail</strong> - Zoom in to instantly see how areas connect, including
        detailed spline information.
      </li>
    </ul>

    <p>To showcase some of the new features, please check out one of the following demos:</p>

    <ul style={releaseNoteListStyle}>
      <li>
        <Link
          href='https://thive.sharepoint.com/:v:/r/sites/Teehive/Gedeelde%20documenten/General/04.%20PI%20sessions/PI-2026-Q4%20Gothenburg/ReportIn/Vulcan/Digital%20twin%204.0%20V1.mov?d=w903484c44b0a4a98873d4f7b4142d929&csf=1&web=1&e=J2I24d'
          target='_blank'
          rel='noopener noreferrer'
          underline='always'
        >
          Digital twin 4.0.mov
        </Link>{' '}
        - Full video
      </li>
      <li>
        <Link
          href='https://thive.sharepoint.com/:v:/r/sites/Teehive/Gedeelde%20documenten/General/04.%20PI%20sessions/PI-2026-Q4%20Gothenburg/ReportIn/Vulcan/Digital%20twin%204.0%20V1-%20Reference%20image%20%26%20Origin.mov?d=w59b8cfa0f5b240f6a9d4707bf9b2cdac&csf=1&web=1&e=4ttMCS'
          target='_blank'
          rel='noopener noreferrer'
          underline='always'
        >
          Reference image & origin.mov
        </Link>
      </li>
      <li>
        <Link
          href='https://thive.sharepoint.com/:v:/r/sites/Teehive/Gedeelde%20documenten/General/04.%20PI%20sessions/PI-2026-Q4%20Gothenburg/ReportIn/Vulcan/Digital%20twin%204.0%20V1-%20Basic%20constallation.mov?d=w597d14e57a8a4b75836f72c7ba3cd6bd&csf=1&web=1&e=PVzFMo'
          target='_blank'
          rel='noopener noreferrer'
          underline='always'
        >
          Basic constellation.mov
        </Link>
      </li>
      <li>
        <Link
          href='https://thive.sharepoint.com/:v:/r/sites/Teehive/Gedeelde%20documenten/General/04.%20PI%20sessions/PI-2026-Q4%20Gothenburg/ReportIn/Vulcan/Digital%20twin%204.0%20V1-%20External%20flows.mov?d=w1021d66fae2d44c98ab47d216ad814e8&csf=1&web=1&e=fId9LH'
          target='_blank'
          rel='noopener noreferrer'
          underline='always'
        >
          External flows.mov
        </Link>
      </li>
      <li>
        <Link
          href='https://thive.sharepoint.com/:v:/r/sites/Teehive/Gedeelde%20documenten/General/04.%20PI%20sessions/PI-2026-Q4%20Gothenburg/ReportIn/Vulcan/Digital%20twin%204.0%20V1-%20Omni.mov?d=wafb6facf09204612afb1ad68491b51b4&csf=1&web=1&e=EbBFtm'
          target='_blank'
          rel='noopener noreferrer'
          underline='always'
        >
          Omnidrive.mov
        </Link>
      </li>
      <li>
        <Link
          href='https://thive.sharepoint.com/:v:/r/sites/Teehive/Gedeelde%20documenten/General/04.%20PI%20sessions/PI-2026-Q4%20Gothenburg/ReportIn/Vulcan/Digital%20twin%204.0%20V1-%20Connections.mov?d=wad06ab78095d4e7c9878415ce59b4da3&csf=1&web=1&e=1e7SVd'
          target='_blank'
          rel='noopener noreferrer'
          underline='always'
        >
          Connections.mov
        </Link>
      </li>
      <li>
        <Link
          href='https://thive.sharepoint.com/:v:/r/sites/Teehive/Gedeelde%20documenten/General/04.%20PI%20sessions/PI-2026-Q4%20Gothenburg/ReportIn/Vulcan/Digital%20twin%204.0%20V1-%20Level%20of%20detail.mov?d=wd7b8b0c70eaf4768b7585a5fe3708906&csf=1&web=1&e=vmo6KU'
          target='_blank'
          rel='noopener noreferrer'
          underline='always'
        >
          Level of detail.mov
        </Link>
      </li>
      <li>
        <Link
          href='https://thive.sharepoint.com/:v:/r/sites/Teehive/Gedeelde%20documenten/General/04.%20PI%20sessions/PI-2026-Q4%20Gothenburg/ReportIn/Vulcan/Digital%20twin%204.0%20V1-%20jit%20routing%20%26%20Iris.mov?d=w3490fdbeba494f43b7b2881d3811e750&csf=1&web=1&e=e3GH5P'
          target='_blank'
          rel='noopener noreferrer'
          underline='always'
        >
          Jit-routing and iris.mov
        </Link>
      </li>
      <li>
        <Link
          href='https://thive.sharepoint.com/:v:/r/sites/Teehive/Gedeelde%20documenten/General/04.%20PI%20sessions/PI-2026-Q4%20Gothenburg/ReportIn/Vulcan/Digital%20twin%204.0%20V1-%20Manual%20area.mov?d=we0476f4b313a4f8897014caab77bd73d&csf=1&web=1&e=7HoCK1'
          target='_blank'
          rel='noopener noreferrer'
          underline='always'
        >
          Manual area.mov
        </Link>
      </li>
    </ul>
  </Typography>
);

const Release4p0p0 = memo(Release4p0p0Component);

const Release3p12p0Component = () => (
  <>
    <Typography sx={releaseNoteTextSx}>
      {`* Consolidation flows for Shuttle Racks\n* Goods-to-person flows for Lassie Systems\n\nFor a full overview of the new features, please check out our article on the portal.`}
    </Typography>
    <Release3p12p0CompatibilitySteps />
  </>
);

const Release3p12p0 = memo(Release3p12p0Component);

const Release3p12p0CompatibilityStepsComponent = () => (
  <>
    <Typography sx={releaseNoteTextSx}>
      {`Previously, there were constraints on the number of columns in a Shuttle Rack and the number of bays in a Lassie System. These constraints have now been removed. You can now create narrow but deep Shuttle Racks and Lassie Systems with as few as one bay.\n\nAs a result, some existing floorplans may no longer validate. If you encounter this issue, please take the following steps:\n* Regenerate the Shuttle Racks\n* Regenerate the Lassie Systems\n* Refresh your UVTs in the setup tab (see image below)`}
    </Typography>
    <img src='/releaseImage3.12_uvt.png' alt='' />
    <Typography sx={releaseNoteListTextSx}>
      * Check the direction of your very narrow aisle racks (see image below)
    </Typography>
    <img src='/releaseImage3.12_racks.png' alt='' />
    <Typography sx={releaseNoteListTextSx}>
      If your floorplan still does not validate after these steps, please submit a support request
      via Freshdesk.
    </Typography>
  </>
);

const Release3p12p0CompatibilitySteps = memo(Release3p12p0CompatibilityStepsComponent);

const Release3p13p0Component = () => (
  <>
    <Typography sx={releaseNoteTextSx}>
      {
        '* Goods-to-Person Flow Simulation (Lassie)\n* Changed under-racking Lassie mappings to accommodate T-FMS\n* Fine-positioning and QR code upload for Lassie BOISE Test\n* Removed logical spaces for Lassie BOISE template\n* Side-loading for one-sided racks (Fleet Batch)'
      }
    </Typography>
    <Release3p12p0CompatibilitySteps />
  </>
);

const Release3p13p0 = memo(Release3p13p0Component);

const Release3p14p0Component = () => (
  <Typography component='div' sx={releaseNoteListTextSx}>
    <ul style={releaseNoteListStyle}>
      <li>
        Traffic Rules – This is a new option on Logical Spaces, where you can set max speeds,
        do-not-stop zones and vehicle modes! Check out the video{' '}
        <Link href={trafficRulesUrl} target='_blank' rel='noopener noreferrer' underline='always'>
          here
        </Link>
        .
      </li>
      <li>
        Sprinklers configuration – For Lassie systems you have the option to offset rows where
        sprinklers could be in the way. Check out the video{' '}
        <Link href={sprinklersUrl} target='_blank' rel='noopener noreferrer' underline='always'>
          here
        </Link>
        .
      </li>
      <li>Many updates on FleetTracker bugs and stability.</li>
      <li>Added new vehicles. The UR01 and the LR01 (undisclosed for now)</li>
      <img src='/releaseImage3.14_vehicles.png' alt='' />
    </ul>
  </Typography>
);

const Release3p14p0 = memo(Release3p14p0Component);

const Release3p15p0Component = () => (
  <Typography component='div' sx={releaseNoteListTextSx}>
    <ul style={releaseNoteListStyle}>
      <li>
        Languages - For the time being, we will only continue support for the English language.
      </li>
      <li>
        Unified layout
        <ul>
          <li>
            Added &quot;restricted access&quot; as a Traffic rule, which is exported to the UL.
          </li>
          <li>Added a suffix to the angled highway segments in the UL for easier distinction.</li>
        </ul>
      </li>
      <li>
        (consolidation) Flow restrictions - Prevent starting a flow from a non-logical space (like
        parking/charging) and added a warning when trying to do so.
      </li>
      <li>2EP Process load durations - Add load/unload durations for 2EP processes.</li>
      <li>
        Bug fixing - We have tackled a number of bugs, including the following;
        <ul>
          <li>Invalid shape pattern visual fix - Added scaling for when shapes were too small.</li>
          <li>Lassie restrictions - Added minimum value that caused crashes.</li>
          <li>Intake racks - Fixed using intake with type storage rack.</li>
          <li>
            2 endpoint processing - Fix side loading (beware, Point of interest will not show in DT
            for now, use navigation)
          </li>
        </ul>
      </li>
    </ul>
  </Typography>
);

const Release3p15p0 = memo(Release3p15p0Component);

export const ReleaseNotesModal = memo(ReleaseNotesModalComponent);
